/**
 * Configuration d'exécution du back-office.
 *
 * Le mode maquette est ici et pas ailleurs : il doit pouvoir être décidé au chargement
 * (variable de build) ET basculé à chaud par un développeur (localStorage), pour travailler
 * sur l'interface avant que le backend n'existe, puis rebrancher l'API réelle sans rebuild.
 */

/** Clé localStorage du basculement à chaud du mode maquette. */
export const CLE_MAQUETTE = 'genviz.admin.maquette';

const VRAI = new Set(['1', 'true', 'oui', 'on']);
const FAUX = new Set(['0', 'false', 'non', 'off']);

function lireBooleen(valeur: string | null | undefined): boolean | null {
  if (valeur == null) return null;
  const v = valeur.trim().toLowerCase();
  if (VRAI.has(v)) return true;
  if (FAUX.has(v)) return false;
  return null;
}

function lireStockageLocal(cle: string): string | null {
  try {
    return window.localStorage.getItem(cle);
  } catch {
    // Navigation privée ou stockage bloqué par politique : on retombe sur la valeur de build.
    return null;
  }
}

/** URL de base de l'API, sans barre oblique finale. Chaîne vide = appels relatifs. */
export const URL_API: string = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '');

/** Étiquette d'environnement affichée dans la barre supérieure. */
export const ENVIRONNEMENT: string = import.meta.env.VITE_APP_ENV ?? 'local';

/** Gabarit de tuiles de la carte de supervision, `null` si aucun fond n'est configuré. */
export const TUILES_URL: string | null = import.meta.env.VITE_MAP_TILES_URL?.trim() || null;

/** Attribution à afficher sous la carte quand un fond de tuiles est configuré. */
export const TUILES_ATTRIBUTION: string =
  import.meta.env.VITE_MAP_TILES_ATTRIBUTION?.trim() || '';

/** Valeur décidée au build : `null` quand la variable est absente (mode automatique). */
const MAQUETTE_BUILD: boolean | null = lireBooleen(import.meta.env.VITE_API_MOCK);

/**
 * Vrai si les appels doivent être servis par le jeu de données factice.
 *
 * PRIORITÉ : variable de build > basculement local > automatique.
 *
 * L'ordre a été INVERSÉ entre le basculement local et la variable de build, et ce n'est pas un
 * détail. Le basculement local est un confort de développeur, écrit dans le stockage du
 * navigateur ; la variable de build est une décision de DÉPLOIEMENT. Laisser le premier
 * l'emporter produit une panne durable et invisible : un poste ayant visité l'application
 * pendant qu'elle tournait sans backend garde le drapeau, et continue de parler à la maquette
 * indéfiniment — même après un déploiement branché sur l'API réelle.
 *
 * Symptôme observé en recette : « Service momentanément indisponible » à la connexion avec un
 * compte pourtant valide, et AUCUNE requête dans le journal nginx. La maquette ne connaît que
 * ses propres comptes de démonstration ; `staff@genviz.fr` lui était inconnu.
 *
 * Le basculement local reste utile en développement, quand aucune décision n'est prise au build
 * — c'est-à-dire exactement le cas où il a été conçu.
 */
export function modeMaquette(): boolean {
  if (MAQUETTE_BUILD !== null) return MAQUETTE_BUILD;
  const local = lireBooleen(lireStockageLocal(CLE_MAQUETTE));
  if (local !== null) return local;
  return URL_API === '';
}

/** Bascule le mode maquette et recharge l'application (le cache de requêtes devient caduc). */
export function definirModeMaquette(actif: boolean): void {
  try {
    window.localStorage.setItem(CLE_MAQUETTE, actif ? '1' : '0');
  } catch {
    // Sans stockage, la bascule ne survit pas au rechargement : on recharge quand même.
  }
  window.location.reload();
}

/** Vrai si l'environnement courant est une production (bandeaux et garde-fous renforcés). */
export function estProduction(): boolean {
  return ENVIRONNEMENT.toLowerCase().startsWith('prod');
}

/** Fuseau d'affichage : celui du poste de l'opérateur, jamais une valeur figée côté serveur. */
export const FUSEAU_AFFICHAGE: string =
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Paris';

/** Cadence de rafraîchissement du tableau de bord, en millisecondes. */
export const RAFRAICHISSEMENT_TABLEAU_MS = 30_000;

/** Cadence de rafraîchissement de la carte de supervision, en millisecondes. */
export const RAFRAICHISSEMENT_CARTE_MS = 60_000;

/** Taille de page par défaut des listes paginées par curseur. */
export const TAILLE_PAGE = 50;
